import type React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { ProjectHeader } from './ProjectHeader';
import { LineItem } from './LineItem';
import type { ProjectData, LineItemData } from './LineItemList';
import type { FlatItem } from './types';
import type { SubItemForm } from './subItemTypes';

interface ProjectZoneProps {
  projectId: string;
  projectData: ProjectData;
  memberItems: FlatItem[];
  collapsed: boolean;
  isDropTarget?: boolean;
  selectedItemId: string | null;
  autoFocusItemId?: string | null;
  onAutoFocusComplete?: () => void;
  onSelectItem: (id: string | null) => void;
  onDeleteLineItem: (id: string) => void;
  onCategoryChange: (id: string, category: string) => void;
  onDescriptionChange: (id: string, description: string) => void;
  onItemDateChange?: (id: string, itemDate: string) => void;
  onToggleCollapse: () => void;
  onRenameProject: (name: string) => void;
  onDeleteProject: () => void;
  onAddLineItemToProject: () => void;
  expandedItemId: string | null;
  readOnly?: boolean;
  overId?: string | null;
  dragActiveId?: string | null;
  startingItemNumber?: number;
  renderAccordionBody?: (item: LineItemData) => React.ReactNode;
  checkedIds?: Set<string>;
  onCheckItem?: (id: string, checked: boolean) => void;
  onCheckProject?: (projectId: string, checked: boolean) => void;
  projectCheckState?: { isChecked: boolean; isIndeterminate: boolean };
  computeItemTotal?: (subItems: SubItemForm[]) => number;
  useRingHighlight?: boolean;
}

export function ProjectZone({
  projectId,
  projectData,
  memberItems,
  collapsed,
  selectedItemId,
  autoFocusItemId,
  onAutoFocusComplete,
  onSelectItem,
  onDeleteLineItem,
  onCategoryChange,
  onDescriptionChange,
  onItemDateChange,
  onToggleCollapse,
  onRenameProject,
  onDeleteProject,
  onAddLineItemToProject,
  expandedItemId,
  readOnly = false,
  isDropTarget = false,
  overId,
  dragActiveId,
  startingItemNumber = 1,
  renderAccordionBody,
  checkedIds,
  onCheckItem,
  onCheckProject,
  projectCheckState,
  computeItemTotal,
  useRingHighlight,
}: ProjectZoneProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id: projectId, disabled: readOnly, animateLayoutChanges: () => false });

  // Don't apply transform/transition — drop position shown by blue line indicator
  const style = {
    opacity: isDragging ? 0.4 : 1,
  };

  const zoneClassName = [
    'border rounded-lg overflow-hidden mb-1.5 transition-colors',
    isDropTarget
      ? 'border-blue-500 ring-2 ring-blue-400/50 bg-blue-50/10'
      : 'border-indigo-700/50',
  ].join(' ');

  return (
    <div
      style={style}
      className={zoneClassName}
    >
      {/* Header section — sortable ref is here so the measurement rect is header-sized */}
      <div ref={setNodeRef} className="bg-muted/50 border-b border-border">
        <ProjectHeader
          project={projectData}
          isCollapsed={collapsed}
          memberCount={memberItems.length}
          memberSubtotal={memberItems.reduce((sum, item) => {
            const li = item.data as LineItemData;
            return sum + (computeItemTotal ? computeItemTotal(li.subItems) : li.totalCost);
          }, 0)}
          onToggleCollapse={onToggleCollapse}
          onRename={onRenameProject}
          onDelete={onDeleteProject}
          onAddLineItem={onAddLineItemToProject}
          readOnly={readOnly}
          dragHandleAttributes={attributes}
          dragHandleListeners={listeners}
          isDragging={isDragging}
          isChecked={projectCheckState?.isChecked}
          isIndeterminate={projectCheckState?.isIndeterminate}
          onCheckedChange={onCheckProject ? (checked) => onCheckProject(projectId, checked) : undefined}
        />
      </div>

      {/* Member items — only rendered when expanded */}
      {!collapsed && (
        <div className="p-1 space-y-1">
          {memberItems.map((flatItem, index) => {
            const lineItemData = flatItem.data as LineItemData;
            const showDropLine = dragActiveId !== null && overId === flatItem.id && overId !== dragActiveId;
            const isExpanded = expandedItemId === flatItem.id;
            return (
              <div key={flatItem.id}>
                {showDropLine && (
                  <div className="h-0.5 bg-blue-500 rounded-full mx-1 my-0.5 shadow-[0_0_4px_rgba(59,130,246,0.5)]" />
                )}
                <LineItem
                  id={flatItem.id}
                  itemNumber={startingItemNumber + index}
                  category={lineItemData.category}
                  description={lineItemData.description}
                  totalCost={computeItemTotal ? computeItemTotal(lineItemData.subItems) : lineItemData.totalCost}
                  subItemCount={lineItemData.subItems?.length ?? 0}
                  isSelected={selectedItemId === flatItem.id}
                  isExpanded={isExpanded}
                  autoOpenCategory={autoFocusItemId === flatItem.id}
                  onAutoOpenComplete={onAutoFocusComplete}
                  onClick={() => onSelectItem(flatItem.id)}
                  onDelete={() => onDeleteLineItem(flatItem.id)}
                  onCategoryChange={(category) => onCategoryChange(flatItem.id, category)}
                  onDescriptionChange={(description) => onDescriptionChange(flatItem.id, description)}
                  itemDate={lineItemData.itemDate}
                  onItemDateChange={onItemDateChange ? (date) => onItemDateChange(flatItem.id, date) : undefined}
                  readOnly={readOnly}
                  isChecked={checkedIds?.has(flatItem.id)}
                  onCheckedChange={onCheckItem ? (checked) => onCheckItem(flatItem.id, checked) : undefined}
                  useRingHighlight={useRingHighlight}
                >
                  {isExpanded && renderAccordionBody?.(lineItemData)}
                </LineItem>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
